import { useState } from 'react';
import { Box, CircularProgress, Paper, Typography } from '@mui/material';
import { useQuery } from '@tanstack/react-query';
import { Navigate, useSearchParams } from 'react-router-dom';
import BASE_DEV_HOST from 'common/config/api';
import { buildSignInUrl, getStoredAccessToken } from 'pages/Auth/utils';
import { fetchOAuthClientInfo } from './oauthClientInfo';
import OAuthConsentScreen from './OAuthConsentScreen';

interface AuthorizeParams {
  clientId: string;
  redirectUri: string;
  responseType: string;
  codeChallenge: string;
  codeChallengeMethod: string;
  state: string;
  scope: string;
}

function parseAuthorizeParams(searchParams: URLSearchParams): AuthorizeParams | null {
  const clientId = searchParams.get('client_id');
  const redirectUri = searchParams.get('redirect_uri');
  const responseType = searchParams.get('response_type');
  const codeChallenge = searchParams.get('code_challenge');
  const codeChallengeMethod = searchParams.get('code_challenge_method');
  const state = searchParams.get('state');

  if (!clientId || !redirectUri || !responseType || !codeChallenge || !codeChallengeMethod || !state) {
    return null;
  }

  return {
    clientId,
    redirectUri,
    responseType,
    codeChallenge,
    codeChallengeMethod,
    state,
    scope: searchParams.get('scope') ?? 'mcp',
  };
}

async function requestAuthorizationCode(
  accessToken: string,
  params: AuthorizeParams,
): Promise<{ code: string; state: string }> {
  const res = await fetch(`${BASE_DEV_HOST}/oauth/authorize`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${accessToken}`,
    },
    body: JSON.stringify({
      client_id: params.clientId,
      redirect_uri: params.redirectUri,
      response_type: params.responseType,
      code_challenge: params.codeChallenge,
      code_challenge_method: params.codeChallengeMethod,
      state: params.state,
      scope: params.scope,
    }),
  });

  if (!res.ok) {
    const body = (await res.json().catch(() => ({}))) as {
      error?: string;
      error_description?: string;
      details?: Record<string, string>;
    };
    const message =
      body.error_description
      ?? (body.details ? Object.values(body.details).join(' ') : null)
      ?? 'Failed to authorize. Please try again.';
    throw new Error(message);
  }

  return (await res.json()) as { code: string; state: string };
}

const OAuthAuthorize = () => {
  const [searchParams] = useSearchParams();

  const [isConnecting, setIsConnecting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const params = parseAuthorizeParams(searchParams);

  const { data: clientInfo, isLoading: isClientLoading } = useQuery({
    queryKey: ['oauth-client', params?.clientId],
    queryFn: () => fetchOAuthClientInfo(params!.clientId),
    enabled: !!params?.clientId,
    staleTime: 60_000,
  });

  const accessToken = getStoredAccessToken();

  if (!accessToken) {
    return (
      <Navigate
        to={buildSignInUrl({
          returnTo: `${window.location.pathname}${window.location.search}`,
        })}
        replace
      />
    );
  }

  if (!params) {
    return (
      <Box display="flex" alignItems="center" justifyContent="center" minHeight="100vh">
        <Paper elevation={3} sx={{ p: 4, maxWidth: 420, textAlign: 'center' }}>
          <Typography variant="h6" color="error" gutterBottom>
            Invalid request
          </Typography>
          <Typography variant="body2" color="text.secondary">
            This authorization link is missing required parameters. Please re-launch the application
            to generate a new link.
          </Typography>
        </Paper>
      </Box>
    );
  }

  const clientName = clientInfo?.name ?? 'This application';

  const handleAllow = async () => {
    setIsConnecting(true);
    setError(null);

    try {
      const result = await requestAuthorizationCode(accessToken, params);
      const redirectUrl = new URL(params.redirectUri);
      redirectUrl.searchParams.set('code', result.code);
      redirectUrl.searchParams.set('state', result.state);
      window.location.href = redirectUrl.toString();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Something went wrong. Please try again.');
      setIsConnecting(false);
    }
  };

  const handleDeny = () => {
    const redirectUrl = new URL(params.redirectUri);
    redirectUrl.searchParams.set('error', 'access_denied');
    redirectUrl.searchParams.set('state', params.state);
    window.location.href = redirectUrl.toString();
  };

  if (isClientLoading) {
    return (
      <Box display="flex" alignItems="center" justifyContent="center" minHeight="100vh">
        <CircularProgress />
      </Box>
    );
  }

  return (
    <OAuthConsentScreen
      clientInfo={clientInfo}
      clientName={clientName}
      error={error}
      isConnecting={isConnecting}
      onAllow={handleAllow}
      onDeny={handleDeny}
    />
  );
};

export default OAuthAuthorize;
